import { s } from '@w/schema'
import type { FlashcardsFeRouterCtx } from '../router/flashcards-fe.router-ctx'
import { writeWorkspace } from '../db/workspace-write'
import { restorePageDeletion } from '../db/learner-actions'

const trashedPageSchema = s.object({ id: s.string(), kind: s.string(), deletedAt: s.number().nullable(), parentId: s.string().nullable() })

export function canChangeTrashedPage(page: { kind: string; deletedAt: number | null } | undefined, level: number, action: 'restore' | 'purge' | 'cancel') {
  return action !== 'cancel' && page !== undefined && page.deletedAt !== null && page.kind !== 'personalRoot' && level >= (action === 'purge' ? 3 : 2)
}

/** Leaf lifecycle is checked independently by trash-bend/oracle.bend. Server
 * rechecks current stored state and authority before publishing the transaction. */
export async function changeTrashedPage(ctx: FlashcardsFeRouterCtx, input: { spaceId: string; pageId: string; level: number; action: 'restore' | 'purge' }) {
  if (input.level < (input.action === 'purge' ? 3 : 2)) throw new Error('Permission denied')
  await ctx.writes.flush(input.spaceId)
  return writeWorkspace(ctx, input.spaceId, () => ctx.db(input.spaceId).transaction(async tx => {
    const page = await tx.query`SELECT id, kind, deletedAt,
      (SELECT fromPageId FROM pageRelations WHERE toPageId=pages.id AND type='child') AS parentId
      FROM pages WHERE id=${input.pageId}`.executeOne(trashedPageSchema)
    if (!canChangeTrashedPage(page, input.level, input.action) || !page || page.kind === 'databaseRow' || !page.parentId) throw new Error('Page is not in Trash')
    if (input.action === 'restore') {
      await restorePageDeletion(tx, { pageId: page.id, userId: ctx.auth.getUser().id, opId: String(page.deletedAt) })
      return
    }
    const child = await tx.query`SELECT ${tx.pageRelations.toPageId} AS id FROM ${tx.pageRelations}
      WHERE ${tx.pageRelations.fromPageId.eq(page.id)} AND ${tx.pageRelations.type.eq('child')} LIMIT 1`.executeOne({ id: tx.pageRelations.toPageId })
    if (child) throw new Error('Move or permanently delete child pages first.')
    // Explicit product rows: trigger side effects alone are not a publication contract.
    await tx.query`DELETE FROM ${tx.pageRelations} WHERE ${tx.pageRelations.fromPageId.eq(page.id)} OR (${tx.pageRelations.toPageId.eq(page.id)} AND ${tx.pageRelations.type.eq('child')})`.run()
    await tx.query`DELETE FROM ${tx.pageTranslations} WHERE ${tx.pageTranslations.pageId.eq(page.id)}`.run()
    await tx.query`DELETE FROM ${tx.pagePermissions} WHERE ${tx.pagePermissions.pageId.eq(page.id)}`.run()
    await tx.query`DELETE FROM ${tx.pages} WHERE ${tx.pages.id.eq(page.id)}`.run()
  }))
}
